import { useEffect, useState } from "react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Check } from "lucide-react";
import { cn } from "@/utils/styles";

type FilterItem = string | number | { label: string; value: number | string };

type MultipleSelectFilterWithSearchProps = {
  title: string;
  name: string;
  options: FilterItem[] | undefined;
  selectedOptions: (string | number)[] | undefined;
  onSelect: (name: string, selected: string | number) => void;
  includeSearch?: boolean;
};

export const MultipleSelectFilterWithSearch: React.FC<MultipleSelectFilterWithSearchProps> = ({
  title,
  name,
  options = [],
  selectedOptions,
  onSelect,
  includeSearch = true
}) => {
  const [filteredOptions, setFilteredOptions] = useState<FilterItem[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);

    if (value === "") {
      setFilteredOptions(options);
      return;
    }

    const filterdData: FilterItem[] = options.filter((option) => {
      if (typeof option === "string") {
        return option.toLowerCase().includes(value.toLowerCase());
      }

      if (typeof option === "object") {
        return option.label.toLowerCase().includes(value.toLowerCase());
      }

      return false;
    });
    setFilteredOptions(filterdData);
  };

  useEffect(() => {
    if (options && options.length > 0) {
      setFilteredOptions(options);
    }
  }, [options]);

  return (
    <Accordion type="single" className="w-full" orientation="horizontal" collapsible>
      <AccordionItem value={name} className="border-0">
        <AccordionTrigger className="label-small text-gray-dark-1000 hover:no-underline">
          {title}
        </AccordionTrigger>
        <AccordionContent className="flex flex-col gap-4">
          {includeSearch && (
            <Input
              placeholder="Search"
              className="subtext-x-small placeholder:subtext-x-small ring-0 focus:border-gray-dark-200 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 "
              value={searchValue}
              onChange={handleSearch}
            />
          )}
          <div className="custom-scrollbar subtext-x-small max-h-[250px] overflow-y-auto overflow-x-hidden text-gray-dark-1000">
            {filteredOptions?.map((option) => {
              const isSelected = selectedOptions?.includes(
                typeof option === "object" ? option.value : option
              );
              return (
                <div
                  key={
                    typeof option === "string" || typeof option === "number" ? option : option.value
                  }
                  className={cn(
                    "flex cursor-pointer items-center justify-between rounded p-2",
                    isSelected ? "bg-gray-dark-500 text-foreground" : "hover:bg-gray-dark-500"
                  )}
                  onClick={() => onSelect(name, typeof option === "object" ? option.value : option)}
                >
                  <span className={isSelected ? "bg-gray-dark-500" : ""}>
                    {typeof option === "object" ? option.label : option}
                  </span>
                  {isSelected && <Check className="h-4 w-4 text-foreground" />}
                </div>
              );
            })}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
